import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import { Certificate, Upload, CheckCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LicenseRenewal() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    state: 'PA',
    license_number: '',
    phone: '',
    company: '',
    notes: '',
    is_looking_for_work: false
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/license-renewal/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Submission failed');
      }

      setSubmitted(true);
      toast.success('Submission received! We\'ll contact you with your free credit mapping report.');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const states = ['PA', 'OH', 'VA', 'WV', 'MD', 'NY', 'NJ', 'DE'];

  if (submitted) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-2xl mx-auto px-6 py-16 text-center">
          <CheckCircle size={80} weight="fill" className="text-primary mx-auto mb-6" />
          <h1 className="text-4xl font-bold text-text_dark mb-4">
            Thank You!
          </h1>
          <p className="text-lg text-text_muted mb-8">
            We&apos;ve received your information. Our team will analyze your license requirements
            and send you a personalized credit mapping report within 24-48 hours.
          </p>
          <div className="bg-accent/10 border-2 border-accent rounded-lg p-6 text-left">
            <h3 className="font-bold text-text_dark mb-3">What Happens Next?</h3>
            <ul className="space-y-2 text-sm text-text_muted">
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">✓</span>
                <span>We&apos;ll review your state&apos;s specific renewal requirements</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">✓</span>
                <span>Create a personalized credit tracking dashboard</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">✓</span>
                <span>Show you exactly which courses you need</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">✓</span>
                <span>Set up automatic credit tracking as you complete courses</span>
              </li>
            </ul>
          </div>
          <button
            onClick={() => window.location.href = '/'}
            className="mt-8 px-8 py-3 bg-primary text-white rounded-sm hover:bg-primary/90 transition-colors"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-primary to-primary/80 text-text_light py-20">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <Certificate size={64} weight="fill" className="text-accent mx-auto mb-6" />
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
            Never Miss a License Renewal
          </h1>
          <p className="text-xl sm:text-2xl max-w-3xl mx-auto leading-relaxed">
            Upload your state-compliant dashboard for a <strong className="text-accent">FREE credit mapping report</strong>
          </p>
        </div>
      </div>

      {/* Value Props */}
      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          <div className="text-center p-6 bg-white rounded-lg shadow-sm border border-borders">
            <div className="text-primary text-4xl mb-3">📊</div>
            <h3 className="font-bold text-lg text-text_dark mb-2">Track All Credits</h3>
            <p className="text-sm text-text_muted">
              Automatically track CORE, Category-specific, and all required credits for your state
            </p>
          </div>
          <div className="text-center p-6 bg-white rounded-lg shadow-sm border border-borders">
            <div className="text-primary text-4xl mb-3">🎓</div>
            <h3 className="font-bold text-lg text-text_dark mb-2">State-Specific Courses</h3>
            <p className="text-sm text-text_muted">
              Access only PA, OH, VA, etc. approved courses that count toward YOUR renewal
            </p>
          </div>
          <div className="text-center p-6 bg-white rounded-lg shadow-sm border border-borders">
            <div className="text-primary text-4xl mb-3">✅</div>
            <h3 className="font-bold text-lg text-text_dark mb-2">Instant Credit Posting</h3>
            <p className="text-sm text-text_muted">
              Complete a quiz, receive credits immediately. No waiting, no manual tracking
            </p>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white rounded-lg shadow-lg border border-borders p-8">
          <h2 className="text-2xl font-bold text-text_dark mb-2 text-center">
            Get Your Free Credit Mapping Report
          </h2>
          <p className="text-center text-text_muted mb-8">
            Tell us about your license and we&apos;ll create a personalized renewal dashboard
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-text_dark mb-2">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-borders rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="John Smith"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text_dark mb-2">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-borders rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text_dark mb-2">
                  State <span className="text-red-500">*</span>
                </label>
                <select
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-borders rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {states.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-text_dark mb-2">
                  License Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="license_number"
                  value={formData.license_number}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-borders rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="PA-12345"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text_dark mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-borders rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="(412) 555-1234"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text_dark mb-2">
                  Company
                </label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-borders rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Your Pest Control Co."
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-text_dark mb-2">
                Additional Notes (Optional)
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={3}
                className="w-full px-4 py-2 border border-borders rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Tell us about your renewal needs, specific categories, or any questions..."
              />
            </div>

            <div className="bg-blue-50 p-4 rounded-sm border border-blue-200">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="is_looking_for_work"
                  checked={formData.is_looking_for_work}
                  onChange={(e) => setFormData({ ...formData, is_looking_for_work: e.target.checked })}
                  className="mt-1"
                />
                <div>
                  <span className="font-medium text-text_dark">
                    I&apos;m looking for pest control work opportunities
                  </span>
                  <p className="text-xs text-text_muted mt-1">
                    Check this box to be included in our job board matchmaking engine. Verified license holders will be visible to employers in your state.
                  </p>
                </div>
              </label>
            </div>

            <div className="bg-gray-50 p-4 rounded-sm border border-borders">
              <div className="flex items-start gap-3">
                <Upload size={24} className="text-primary flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-text_dark mb-1">
                    Optional: Upload Your Current Dashboard
                  </p>
                  <p className="text-xs text-text_muted mb-3">
                    Have a PDF or screenshot of your state&apos;s licensing dashboard? Upload it for even more accurate credit mapping.
                  </p>
                  <p className="text-xs text-text_muted italic">
                    File upload coming soon - for now, just submit your info and we&apos;ll follow up
                  </p>
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full px-8 py-4 bg-primary text-white rounded-sm hover:bg-primary/90 transition-colors font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Submitting...' : 'Get My Free Credit Report →'}
            </button>

            <p className="text-xs text-center text-text_muted">
              By submitting, you agree to receive your free credit report and course recommendations via email.
            </p>
          </form>
        </div>

        {/* Social Proof */}
        <div className="mt-12 text-center">
          <p className="text-sm text-text_muted mb-4">
            <strong>Join 500+ Pennsylvania applicators</strong> who never miss a renewal deadline
          </p>
          <div className="flex items-center justify-center gap-8 flex-wrap">
            <div className="text-center">
              <div className="text-2xl font-bold text-primary">12,000+</div>
              <div className="text-xs text-text_muted">Credits Tracked</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-primary">98%</div>
              <div className="text-xs text-text_muted">On-Time Renewals</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-primary">24hrs</div>
              <div className="text-xs text-text_muted">Report Turnaround</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
